import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ChevronRight, Database, ExternalLink, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { MasterDriftResolver } from '~/components/conflicts';
import {
  EmptyState,
  ExportMenu,
  ListPageShell,
  PageHeader,
  SavedViewsBar,
  Table,
} from '~/components/data';
import { AIInsightCard } from '~/components/domain';
import { ConflictCard } from '~/components/domain';
import { useToast } from '~/components/feedback';
import { Button, Drawer, Select, TraceChip } from '~/components/primitives';
import { StatusBadge } from '~/components/status';
import { type ConflictFilter, fetchConflicts, resolveConflict } from '~/lib/api/conflicts';
import { useListPageState } from '~/lib/hooks/useListPageState';
import { useShortcut } from '~/lib/shortcuts/useShortcuts';
import { useActiveEntityStore } from '~/store/active-entity';
import { useAuthStore } from '~/store/auth';
import { useUiStore } from '~/store/ui';
import shared from './_auth._shared.module.css';

// FE-1: detect broken-data conflicts where side values are missing/undefined.
// BE-N может return `'undefined'` strings от legacy stringified payload — детектим оба варианта.
function isBrokenConflict(c: {
  source_a?: unknown;
  source_b?: unknown;
  subject?: unknown;
  side_a?: { value?: unknown };
  side_b?: { value?: unknown };
}): boolean {
  const isBroken = (v: unknown): boolean =>
    v === undefined || v === null || v === '' || v === 'undefined' || v === 'null';
  // v0.3.205 fix: broken-state surfaces inside ConflictCard через side.value,
  // не через source/subject (которые в seeded data populated).
  return (
    isBroken(c.side_a?.value) ||
    isBroken(c.side_b?.value) ||
    isBroken(c.source_a) ||
    isBroken(c.source_b) ||
    isBroken(c.subject)
  );
}

export const Route = createFileRoute('/_auth/conflicts')({
  staticData: { minMainWidth: 960 },
  component: ConflictsInbox,
});

function ConflictsInbox(): JSX.Element {
  const [filter, setFilter] = useState<ConflictFilter>({});
  const [selectedIdx, setSelectedIdx] = useState<number>(-1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();
  const currentUser = useAuthStore((s) => s.user);
  // FE-2: auto-collapse SideNav при открытии drawer (Designing for Intent).
  // Restore previous state on drawer close.
  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed);
  const setSidebarCollapsed = useUiStore((s) => s.setSidebarCollapsed);
  const prevCollapsedRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (drawerOpen) {
      if (prevCollapsedRef.current === null) {
        prevCollapsedRef.current = sidebarCollapsed;
        setSidebarCollapsed(true);
      }
    } else if (prevCollapsedRef.current !== null) {
      setSidebarCollapsed(prevCollapsedRef.current);
      prevCollapsedRef.current = null;
    }
  }, [drawerOpen, sidebarCollapsed, setSidebarCollapsed]);

  const q = useQuery({
    queryKey: ['conflicts', filter],
    queryFn: () => fetchConflicts(filter),
  });
  const state = useListPageState(q);

  const items = q.data?.items ?? [];
  const selected = selectedIdx >= 0 ? items[selectedIdx] : undefined;
  // F-sync — колонка «Компания» только в агрегатном охвате (холдинг/«Все компании»).
  const aggregateScope = useActiveEntityStore((s) => s.activeScope) !== 'entity';
  const entities = q.data?.entities ?? {};

  const resolveMut = useMutation({
    mutationFn: ({ id, winner }: { id: string; winner: 'a' | 'b' }) =>
      resolveConflict(id, winner, 'manual resolve'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conflicts'] });
      setDrawerOpen(false);
    },
  });

  useShortcut(
    'j',
    () => setSelectedIdx((i) => Math.min(i + 1, items.length - 1)),
    m.route_conflicts_shortcut_next(),
  );
  useShortcut(
    'k',
    () => setSelectedIdx((i) => Math.max(i - 1, 0)),
    m.route_conflicts_shortcut_prev(),
  );
  useShortcut(
    'Enter',
    () => {
      if (selected) setDrawerOpen(true);
    },
    m.route_conflicts_shortcut_open(),
  );
  useShortcut(
    'x',
    () => {
      if (selected) {
        // authoritative — это SourceSystem (string), маппим на 'a' | 'b' по позиции в паре.
        const winner: 'a' | 'b' =
          selected.authoritative && selected.authoritative === selected.source_b ? 'b' : 'a';
        resolveMut.mutate({ id: selected.id, winner });
      }
    },
    m.route_conflicts_shortcut_resolve(),
  );
  // FE-1 honest-audit: 'c' = copy trace ID активного drawer'а.
  useShortcut(
    'c',
    () => {
      if (selected) {
        void navigator.clipboard.writeText(selected.id).then(() => {
          toast.push({ variant: 'success', title: m.route_conflicts_trace_copied() });
        });
      }
    },
    m.route_conflicts_shortcut_copy_trace(),
  );

  return (
    <div className={shared.page}>
      <PageHeader
        title={m.nav_conflicts()}
        actions={
          <ExportMenu resource="conflicts" filters={filter as Record<string, string | undefined>} />
        }
      />
      {/* FE-1 / BE-1 follow-up AI Insight: detect severity drift (all items same warn).
          UX designer review flagged что BE-1 default 'warn' создаёт visual signal loss. */}
      {(() => {
        if (items.length < 3) return null;
        const sev = new Set(items.map((i) => i.severity));
        if (sev.size !== 1) return null;
        const onlySev = items[0]?.severity;
        if (onlySev !== 'warn') return null;
        return (
          <AIInsightCard
            title="Сигнал-drift по severity"
            body={
              <span>
                Все {items.length} конфликта помечены одинаковым severity «warn». Это указывает на
                legacy-данные без явного severity (BE default fallback). Рекомендуется backfill
                severity по conflict_type для корректного триажа.
              </span>
            }
          />
        );
      })()}
      {/* FE-1: page-level alert когда ≥1 broken row для quick awareness. */}
      {(() => {
        const broken = items.filter(isBrokenConflict);
        if (broken.length === 0) return null;
        return (
          <div
            style={{
              padding: 'var(--space-3)',
              border: '1px solid var(--color-danger)',
              borderLeft: '3px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--danger-bg-15)',
              marginBottom: 'var(--space-3)',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
            role="alert"
          >
            <Database size={18} color="var(--color-danger)" aria-hidden="true" />
            <strong style={{ color: 'var(--color-danger)' }}>
              {broken.length} {broken.length === 1 ? 'конфликт' : 'конфликта'} с повреждёнными
              данными
            </strong>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                const ids = broken.map((c) => c.id).join('\n');
                void navigator.clipboard.writeText(ids).then(() => {
                  toast.push({
                    variant: 'success',
                    title: m.route_conflicts_trace_copied_bulk({ count: broken.length }),
                  });
                });
              }}
            >
              {m.route_conflicts_broken_copy_trace()} ({broken.length})
            </Button>
          </div>
        );
      })()}
      <SavedViewsBar
        resource="conflicts"
        currentFilters={filter}
        onApply={(v) => setFilter(v.filters as ConflictFilter)}
      />
      <section className={shared.sectionCard}>
        <h3 className={shared.sectionHeading}>{m.route_conflicts_filters_heading()}</h3>
        <div className={shared.formGrid3}>
          <Select
            label={m.route_conflicts_filter_severity()}
            value={filter.severity ?? ''}
            onChange={(e) => setFilter((f) => ({ ...f, severity: e.target.value || undefined }))}
            options={[
              { value: '', label: m.route_conflicts_filter_all() },
              { value: 'low', label: m.route_conflicts_severity_low() },
              { value: 'medium', label: m.route_conflicts_severity_medium() },
              { value: 'high', label: m.route_conflicts_severity_high() },
              { value: 'critical', label: m.route_conflicts_severity_critical() },
            ]}
          />
          <Select
            label={m.route_conflicts_filter_type()}
            value={filter.type ?? ''}
            onChange={(e) => setFilter((f) => ({ ...f, type: e.target.value || undefined }))}
            options={[
              { value: '', label: m.route_conflicts_filter_all() },
              { value: 'intra_level', label: m.route_conflicts_type_intra_level() },
              { value: 'master_data_drift', label: m.route_conflicts_type_master_data_drift() },
              { value: 'valuation_mismatch', label: m.route_conflicts_type_valuation_mismatch() },
            ]}
          />
          <Select
            label={m.route_conflicts_filter_status()}
            value={filter.status ?? ''}
            onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value || undefined }))}
            options={[
              { value: '', label: m.route_conflicts_filter_all() },
              { value: 'open', label: m.route_conflicts_status_open() },
              { value: 'resolved', label: m.route_conflicts_status_resolved() },
              { value: 'escalated', label: m.route_conflicts_status_escalated() },
            ]}
          />
        </div>
      </section>

      <ListPageShell
        state={state}
        emptyTitle={m.route_conflicts_empty_title()}
        emptyDescription={m.route_conflicts_empty_description()}
      >
        {(rows) => (
          <Table
            ariaLabel={m.nav_conflicts()}
            data={rows}
            rowKey={(c) => c.id}
            // FE-141/142 Sprint N+: row-click, StatusBadge severity, ProvenanceChip sources.
            onRowClick={(c) => {
              const idx = rows.findIndex((x) => x.id === c.id);
              setSelectedIdx(idx);
              setDrawerOpen(true);
            }}
            // FE-1 honest-audit: broken rows visibly distinct (bg + border-left).
            rowClassName={(c) => (isBrokenConflict(c) ? 'rowBroken' : undefined)}
            columns={[
              {
                id: 'subject',
                header: m.route_conflicts_col_subject(),
                // FE-190 Pass 46: fallback to «—» вместо пустоты — BE до денорм
                // не возвращает subject. Visible placeholder упрощает scanning.
                accessor: (c) => c.subject || '—',
              },
              ...(aggregateScope
                ? [
                    {
                      id: 'entity',
                      header: m.project_col_entity(),
                      accessor: (c: (typeof rows)[number]) =>
                        (c.entity_id ? entities[c.entity_id]?.name : undefined) ?? '—',
                      width: '180px',
                    },
                  ]
                : []),
              {
                id: 'type',
                header: m.route_conflicts_col_type(),
                // BE-3 honest-audit: localize enum values + mono fallback на raw для unknown types.
                accessor: (c) => {
                  if (!c.type) return '—';
                  const labels: Record<string, string> = {
                    intra_level: m.route_conflicts_type_intra_level(),
                    master_data_drift: m.route_conflicts_type_master_data_drift(),
                    master_data_field_mismatch: m.route_conflicts_type_master_data_drift(),
                    valuation_mismatch: m.route_conflicts_type_valuation_mismatch(),
                    transaction_amount_mismatch: 'Расхождение сумм',
                  };
                  return labels[c.type] ?? c.type;
                },
              },
              {
                id: 'sev',
                header: m.route_conflicts_col_severity(),
                // FE-142: severity through StatusBadge primitive (color + icon).
                accessor: (c) => {
                  const map: Record<
                    string,
                    'completed_draft' | 'needs_review' | 'degraded' | 'escalated'
                  > = {
                    low: 'completed_draft',
                    medium: 'needs_review',
                    high: 'degraded',
                    critical: 'escalated',
                  };
                  const status = (c.severity ? map[c.severity] : undefined) ?? 'needs_review';
                  return <StatusBadge status={status} />;
                },
              },
              {
                id: 'pair',
                header: m.route_conflicts_col_sources(),
                // FE-142: sources as ProvenanceChip-style mono badges.
                // FE-192 Pass 46: title-tooltip раскрывает «Источник A vs Источник B»
                // — иначе «— ↔ —» без context не объяснён.
                accessor: (c) => (
                  <span
                    className={shared.mono}
                    title={`${c.source_a ?? '—'} vs ${c.source_b ?? '—'}`}
                  >
                    {c.source_a ?? '—'} ↔ {c.source_b ?? '—'}
                  </span>
                ),
              },
              {
                id: 'open',
                header: '',
                accessor: () => (
                  <span style={{ color: 'var(--text-muted)' }} aria-hidden="true">
                    <ChevronRight size={16} />
                  </span>
                ),
              },
            ]}
          />
        )}
      </ListPageShell>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selected?.subject ?? m.route_conflicts_drawer_fallback_title()}
        width={520}
        footer={
          selected ? (
            <>
              {/* FE-1: disable Accept buttons если data broken (undefined sources). */}
              <Button
                variant="secondary"
                onClick={() => resolveMut.mutate({ id: selected.id, winner: 'a' })}
                disabled={resolveMut.isPending || isBrokenConflict(selected)}
                title={
                  isBrokenConflict(selected)
                    ? 'Невозможно принять: данные конфликта неполные (см. trace ID в drawer)'
                    : undefined
                }
              >
                {m.route_conflicts_btn_accept_a()}
              </Button>
              <Button
                variant="primary"
                onClick={() => resolveMut.mutate({ id: selected.id, winner: 'b' })}
                disabled={resolveMut.isPending || isBrokenConflict(selected)}
                title={
                  isBrokenConflict(selected)
                    ? 'Невозможно принять: данные конфликта неполные'
                    : undefined
                }
              >
                {m.route_conflicts_btn_accept_b()}
              </Button>
            </>
          ) : null
        }
      >
        {selected && isBrokenConflict(selected) && (
          /* FE-1 honest-audit: EmptyState primitive с Database icon + 3 actions (copy/audit/close). */
          <EmptyState
            icon={<Database size={48} color="var(--color-danger)" />}
            title={m.route_conflicts_broken_title()}
            description={m.route_conflicts_broken_description()}
            action={
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                <TraceChip traceId={selected.id} copiedMessage={m.route_conflicts_trace_copied()} />
                <Link
                  to="/admin/audit"
                  className={shared.link}
                  style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  {m.route_conflicts_broken_open_audit()}
                  <ExternalLink size={12} aria-hidden="true" />
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  leadingIcon={<X size={12} />}
                  onClick={() => setDrawerOpen(false)}
                >
                  {m.route_conflicts_broken_close()}
                </Button>
              </div>
            }
          />
        )}
        {selected && !isBrokenConflict(selected) && (
          <ConflictCard
            conflict={selected as unknown as Parameters<typeof ConflictCard>[0]['conflict']}
          />
        )}
        {/* FE-1 honest-audit: approver context block в footer drawer (для Tier 2 audit trail). */}
        {selected && !isBrokenConflict(selected) && currentUser && (
          <div
            style={{
              marginTop: 'var(--space-4)',
              padding: 'var(--space-3)',
              background: 'var(--color-bg-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 13,
            }}
            role="note"
            aria-label={m.route_conflicts_approver_context_title()}
          >
            <strong style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
              {m.route_conflicts_approver_context_title()}
            </strong>
            <span style={{ color: 'var(--color-text-secondary)' }}>
              {m.route_conflicts_approver_context_body({
                role: currentUser.role,
                email: currentUser.email,
              })}
            </span>
          </div>
        )}
        {selected?.type === 'master_data_drift' && (
          <div className={shared.sectionGap}>
            <h3 className={shared.sectionHeading}>{m.route_conflicts_section_drift_resolver()}</h3>
            <MasterDriftResolver
              conflictId={selected.id}
              onResolved={() => {
                void qc.invalidateQueries({ queryKey: ['conflicts'] });
                setDrawerOpen(false);
              }}
            />
          </div>
        )}
      </Drawer>
    </div>
  );
}
